import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { LDProvider, ProviderConfig } from 'launchdarkly-react-client-sdk';
import { LDContext, LDEvaluationDetail } from 'launchdarkly-js-client-sdk';

import { useSelector } from 'react-redux';
import { useAppConfig } from 'hooks/useAppConfig';
import { localFlagOverrides } from 'hooks/featureFlags/localOverrides';
import { selectUser, selectUserCompany } from '../../redux/user/selectors';

/** The three values the LaunchDarkly context is built from; tenantId is the company code. */
export type FeatureFlagIdentity = {
  tenantId: string;
  userId: string;
  email: string;
};

type SeedFeatureFlagIdentity = (identity: FeatureFlagIdentity) => void;

const FeatureFlagIdentityContext = createContext<SeedFeatureFlagIdentity | undefined>(undefined);

/**
 * Seeds the LaunchDarkly context before a user exists in Redux. The login
 * callback hands it the id_token claims so the per-company auth-mode flag can
 * be read without establishing a session first.
 */
export function useSeedFeatureFlagIdentity(): SeedFeatureFlagIdentity {
  const seedIdentity = useContext(FeatureFlagIdentityContext);

  if (!seedIdentity) {
    throw new Error('useSeedFeatureFlagIdentity must be used within FeatureFlagProvider');
  }

  return seedIdentity;
}

/**
 * e2e users evaluate flags on the shared BCE2E company only, so per-user
 * targeting never reaches the fixtures.
 */
function buildLdContext({ tenantId, userId, email }: FeatureFlagIdentity): LDContext {
  if (email.includes('e2e')) {
    return {
      kind: 'multi',
      company: {
        key: 'BCE2E',
      },
    };
  }

  return {
    kind: 'multi',
    company: {
      key: tenantId,
    },
    user: {
      key: userId,
      email: email.toLowerCase(),
      _meta: {
        privateAttributes: ['email'],
      },
    },
  };
}

/**
 * Builds the LaunchDarkly context from whichever identity exists: the Redux
 * user once it has loaded, otherwise the identity seeded from the id_token on
 * the login callback. Whole-identity precedence, never per-field mixing.
 *
 * LDProvider initializes on the first defined context and ignores later
 * changes, so the seed only matters before the first Redux user lands; once it
 * does, both sources yield the same keys by construction (the id_token `uid`
 * claim is the marketpricing User.id).
 */
export function FeatureFlagProvider({ children }: PropsWithChildren) {
  const user = useSelector(selectUser);
  const userCompany = useSelector(selectUserCompany);
  const [seededIdentity, seedIdentity] = useState<FeatureFlagIdentity>();

  const tenantId = userCompany?.code;
  const userId = user?.id;
  const userEmail = user?.emailAddress;

  const ldUserDetails = useMemo(() => {
    const identity =
      tenantId && userId && userEmail ? { tenantId, userId: String(userId), email: userEmail } : seededIdentity;

    return identity ? buildLdContext(identity) : undefined;
  }, [tenantId, userId, userEmail, seededIdentity]);

  // Subscribed rather than read from the module binding: this provider sits
  // *above* AppConfigProvider in _app.tsx, so it renders before /api/config
  // resolves — and AmsAuthProvider can populate the Redux user from an existing
  // session cookie before it does.
  const appConfig = useAppConfig();

  // LaunchDarkly is in play only where a loaded config carries a clientSideID
  // and no fixed values: a dev environment serves `featureFlags` instead, and
  // its browser must never connect — mirroring DevProvider on the server.
  // Until the config loads, or if it never does, there is no id to connect
  // with, and every flag stays on its default.
  const usesLaunchDarkly = Boolean(appConfig?.launchDarklyClientId) && appConfig?.featureFlags === undefined;

  const providerOptions: ProviderConfig = useMemo(() => {
    return {
      clientSideID: appConfig?.launchDarklyClientId || '',
      deferInitialization: true,
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      options: {
        bootstrap: localFlagOverrides ?? 'localStorage',
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDEvaluationDetail) => {
              if (window.DD_RUM) {
                window.DD_RUM.addFeatureFlagEvaluation(key, detail.value);
              }
            },
          },
        ],
      },
      // Withholding the context is what keeps a dev environment's browser off
      // LaunchDarkly entirely, mirroring DevProvider on the server: with
      // `deferInitialization`, the client only connects once a context exists.
      // It is withheld until the config settles for the same reason — the
      // client keeps the first context it is given, so handing one over early
      // would pin the connection to an empty clientSideID.
      context: usesLaunchDarkly ? ldUserDetails : undefined,
    } as ProviderConfig
  }, [ldUserDetails, appConfig, usesLaunchDarkly]);

  return (
    <FeatureFlagIdentityContext.Provider value={seedIdentity}>
      <LDProvider{...providerOptions}>
          {children}
      </LDProvider>
    </FeatureFlagIdentityContext.Provider>
  );
}
